/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import CardBody from '../../../components/Card/CardBody';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { DateSelection } from '../../../components/Date/DateSelection';
import { useDispatch, useSelector } from 'react-redux';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { LoadPlatformResellersType, PlatformResellerLoader } from '../../../modules/platformreseller/PlatformResellerLoader';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import { DateReducersType } from '../../../modules/date/DateReducers';
import { InvoiceDataInterface } from '../../../interfaces/platform/InvoiceDataInterface';
import { apiCall } from '../../../core/apiCall';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { notificationConstants } from '../../../modules/notifications/notifications';
import CustomSmallTextButton from '../../../components/Buttons/CustomSmallTextButton';
import { documentConstants } from '../../../modules/document/actions';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import InvoiceDataList from './InvoiceDataList';
import { Moment } from 'moment';
import { DateHelper } from '../../../helpers/DateHelper';
import DatePickerForm from '../../../components/Form/DatePickerForm';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import InputForm from '../../../components/Form/InputForm';
import { ReactComponent as Cancel } from '../../../assets/themes/OPL/img/icons/Cancel.svg';
import { VatCollectionInterface } from '../../../interfaces/collections/VatCollectionInterface';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { useVats } from '../../../modules/document/useVats';
import { findVatByPartialIri } from '../../../modules/document/VatHelper';

export type InvoiceAddDialogProps = {
    classes: any;
    openView: boolean;
    fullWidth: boolean;
    title?: string;
    closeCallback: () => void;
    saveCallback: (params: any) => void;
    firstLoad: () => void;
    showMainLoader: (show: boolean) => void;
};

const _ = require('underscore');

export const InvoiceAddDialog = (props: InvoiceAddDialogProps): React.ReactElement => {
    const dispatch = useDispatch();
    const { classes } = props;
    const itemLoader: ItemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const platformResellersLoader: PlatformResellerLoader = useMemo(() => {
        return new PlatformResellerLoader(itemLoader);
    }, [itemLoader]);
    const [file, setFile] = useState<File | null>(null);
    const [resellers, setResellers] = useState<PlatformResellerCollectionInterface | null>(null);
    const [selectedReseller, setSelectedReseller] = useState<number | null>(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState<number | null>(null);
    const [selectedCorrectionType, setSelectedCorrectionType] = useState<number | null>(null);
    const [correctionReason, setCorrectionReason] = useState<string | null>(null);
    const [correctingInvoice, setCorrectingInvoice] = useState<string | null>(null);
    const [handedOn, setHandedOn] = useState<string | null>(null);
    const [soldOn, setSoldOn] = useState<string | null>(null);
    const [paymentDeadline, setPaymentDeadline] = useState<string | null>(null);
    const [costInvoice, setCostInvoice] = useState<string | null>(null);
    const [costBarcode, setCostBarcode] = useState<string | null>('');
    const [manualInvoiceReadData, setManualInvoiceReadData] = useState<Array<InvoiceDataInterface>>([]);
    const [manualInvoiceData, setManualInvoiceData] = useState<Array<InvoiceDataInterface>>([]);
    const { year, month } = useSelector((state: { dateReducers: DateReducersType }) => state.dateReducers);
    const { vats, vatsSelect, triedLoading } = useVats();
    const defaultVat = useMemo((): string|null => {
        if (vats && vats['hydra:member'] && vats['hydra:member'].length > 0) {
            return vats['hydra:member'][0]['@id'];
        }

        return null;
    }, [vats]);

    const selectFile = (event: any): void => {
        const file = event.target.files[0];
        setFile(file);
    };

    const resellersCallback = useCallback((): void => {
        setResellers(platformResellersLoader.getPlatformResellerCollection());
    }, [setResellers, platformResellersLoader]);

    useEffect((): void => {
        const params: LoadPlatformResellersType = {
            callback: resellersCallback,
            filters: [{ name: 'deleted', value: 0 }],
        };
        platformResellersLoader.loadPlatformResellers(params);
    }, [platformResellersLoader, resellersCallback, itemLoader]);

    const selectReseller = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedReseller(event.target.value as number);
        setManualInvoiceData([]);
        setManualInvoiceReadData([]);
        setCorrectingInvoice(null);
        setSelectedCorrectionType(null);
        setCorrectionReason(null);
        // setHandedOn(null);
        // setSoldOn(null);
        // setPaymentDeadline(null);
    };

    const selectDocumentType = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedDocumentType(event.target.value as number);
        setManualInvoiceData([]);
        setManualInvoiceReadData([]);
        setCorrectingInvoice(null);
        setSelectedCorrectionType(null);
        setCorrectionReason(null);
        // setHandedOn(null);
        // setSoldOn(null);
        // setPaymentDeadline(null);
    };

    const selectCorrectionType = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedCorrectionType(event.target.value as number);
        setManualInvoiceData([]);
        setManualInvoiceReadData([]);
        setCorrectingInvoice(null);
        setCorrectionReason(null);
        // setHandedOn(null);
        // setSoldOn(null);
        // setPaymentDeadline(null);
    };

    const saveWithFileCallback = (): void => {
        const params: { type?: string; params?: any } = {};
        if (file && selectedReseller && costInvoice && manualInvoiceData.length > 0 && handedOn && soldOn && paymentDeadline) {
            const data = new FormData();
            data.append('file', file);
            data.append('type', 'invoice.cost');
            data.append(
                'params',
                JSON.stringify({
                    year: year,
                    month: month,
                    type: selectedDocumentType,
                    costInvoice: costInvoice,
                    costCorrectingInvoice: correctingInvoice,
                    invoiceData: manualInvoiceData,
                    buyer: selectedReseller,
                    handedOn: handedOn,
                    soldOn: soldOn,
                    paymentDeadline: paymentDeadline,
                    costBarcode: costBarcode,
                })
            );

            apiCall
                .uploadFile(FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/uploads')), data)
                .then((response: any): void => {
                    setFile(null);
                    dispatch({
                        type: notificationConstants.CUSTOM_NOTIFY,
                        message: 'documents:invoices.upload_success',
                        variant: 'success',
                    });
                    props.closeCallback();
                    props.firstLoad();
                })
                .catch((error: any): any => {
                    dispatch({ type: notificationConstants.ERROR });
                    props.closeCallback();
                    props.firstLoad();
                });
        }
    };

    const saveCallback = (): void => {
        if (selectedDocumentType === 3 || selectedDocumentType === 4) {
            saveWithFileCallback();
            return;
        }

        if (selectedDocumentType === 2 && selectedCorrectionType === 2 && manualInvoiceData.length === 0) {
            NotificationHelper.notifyError('documents:invoices.not_all_data_inserted', dispatch);
            return;
        }

        const params: { type?: string; params?: any } = {};
        params.type = 'invoice';
        params.params = JSON.stringify({
            buyer: selectedReseller,
            type: selectedDocumentType,
            correctionType: selectedCorrectionType,
            correctionReason: correctionReason,
            correctingInvoice: correctingInvoice,
            year: year,
            month: month,
            manualInvoiceData: manualInvoiceData,
        });

        props.saveCallback(params);
    };

    const addInvoiceLine = useCallback((): void => {
        if(!resellers || !vats) {
            return;
        }
        const tmpReseller = resellers['hydra:member'].find((reseller: PlatformResellerInterface) => reseller.id === selectedReseller);
        if(!tmpReseller) {
            return;
        }
        const tmpVat = findVatByPartialIri(vats, tmpReseller.vat);

        console.log(tmpVat, vats, tmpReseller);
        const newLine: InvoiceDataInterface = {
            recordName: '',
            recordCode: -1,
            amount: 0.0,
            noTaxAmount: 0.0,
            taxAmount: 0.0,
            taxPercentage: -1,
            vat: tmpVat ? tmpVat['@id'] : '',
        };
        setManualInvoiceData([...manualInvoiceData, newLine]);
    }, [vats, resellers, selectedReseller, manualInvoiceData]);

    const deleteInvoiceLine = (data: any): void => {
        let lines = [...manualInvoiceData];
        if (lines.length === 1) {
            setManualInvoiceData([]);
            return;
        }
        lines = _.reject(lines, (line: InvoiceDataInterface) => {
            return data.item === line;
        });
        // lines = lines.splice(data.index, 1);
        setManualInvoiceData([...lines]);
    };

    const recalculateValue = (obj: InvoiceDataInterface, column: string, value: any): void => {
        const tmpVat = findVatByPartialIri(vats!, obj.vat);
        if (!tmpVat) {
            return;
        }
        let taxPercentageInt = parseInt(String(tmpVat.percentage));
        let taxPercentage = (100 + (taxPercentageInt < 0 ? 0 : taxPercentageInt)) / 100;

        if (column === 'taxPercentage' || column === 'vat') {
            taxPercentage = (100 + taxPercentageInt) / 100;
            obj.amount = CurrencyHelper.bulletproofRound(
                CurrencyHelper.bulletproofRound(obj.noTaxAmount) * CurrencyHelper.bulletproofRound(taxPercentage),
                2
            ).toFixed(2);
            if (taxPercentageInt === 0) {
                obj.taxAmount = CurrencyHelper.bulletproofRound('0').toFixed(2);
            } else {
                obj.taxAmount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.amount) - CurrencyHelper.bulletproofRound(obj.noTaxAmount),
                    2
                ).toFixed(2);
            }
        }

        if (column === 'amount') {
            if (parseFloat(value) === 0) {
                obj.noTaxAmount = CurrencyHelper.bulletproofRound('0');
                obj.taxAmount = CurrencyHelper.bulletproofRound('0');
            } else {
                obj.noTaxAmount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.amount) / CurrencyHelper.bulletproofRound(taxPercentage),
                    2
                ).toFixed(2);
                obj.taxAmount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.amount) -
                        CurrencyHelper.bulletproofRound(obj.amount) / CurrencyHelper.bulletproofRound(taxPercentage),
                    2
                ).toFixed(2);
            }
        }

        if (column === 'noTaxAmount') {
            if (parseFloat(value) === 0) {
                obj.amount = CurrencyHelper.bulletproofRound('0');
                obj.taxAmount = CurrencyHelper.bulletproofRound('0');
            } else {
                obj.amount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.noTaxAmount) * CurrencyHelper.bulletproofRound(taxPercentage),
                    2
                ).toFixed(2);
                obj.taxAmount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.amount) - CurrencyHelper.bulletproofRound(obj.noTaxAmount),
                    2
                ).toFixed(2);
            }
        }

        if (column === 'taxAmount') {
            if (parseFloat(value) === 0) {
                obj.noTaxAmount = CurrencyHelper.bulletproofRound('0');
                obj.amount = CurrencyHelper.bulletproofRound('0');
            } else {
                obj.noTaxAmount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.taxAmount) / CurrencyHelper.bulletproofRound(taxPercentageInt / 100),
                    2
                ).toFixed(2);
                obj.amount = CurrencyHelper.bulletproofRound(
                    CurrencyHelper.bulletproofRound(obj.noTaxAmount) + CurrencyHelper.bulletproofRound(obj.taxAmount),
                    2
                ).toFixed(2);
            }
        }
    };

    const updateCorrectionItem = (
        index: number,
        column: string,
        event?: React.ChangeEvent<HTMLInputElement>,
        type = 'string',
        value?: string
    ): void => {
        const lines = [...manualInvoiceData];
        const obj = lines[index];
        const key = column as keyof typeof obj;

        let tmpValue: any = '';
        if (event) {
            tmpValue = event.target.value;
        }
        if (value) {
            tmpValue = value;
        }

        if (type === 'number') {
            // if (value === '') {
            //     value = '0';
            // }

            if (String(tmpValue).slice(-1) === ',') {
                tmpValue = tmpValue.replace(',', '.');
            }

            if (tmpValue !== '-' && tmpValue !== '') {
                if (isNaN(parseFloat(tmpValue))) {
                    tmpValue = '0';
                }

                const regex = /^[-]{0,1}[0-9]{0,}[.]{0,1}[0-9]{0,}$/;
                if (!regex.test(tmpValue)) {
                    tmpValue = obj[key];
                }
            }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        obj[key] = tmpValue;
        if (type === 'number' && tmpValue !== '' && selectedDocumentType !== 4 && selectedDocumentType !== 3) {
            recalculateValue(obj, column, tmpValue);
        }
        if(type === 'string' && column === 'vat') {
            recalculateValue(obj, column, tmpValue);
        }
        setManualInvoiceData([...lines]);
    };

    const downloadCorrectedInvoiceData = useCallback((): void => {
        if (!correctingInvoice || !resellers || !vats) {
            return;
        }

        props.showMainLoader(true);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/invoices')),
            documentConstants.COST_REPORT_LIST,
            [
                {
                    name: 'invoiceNumber',
                    value: correctingInvoice,
                },
            ],
            [],
            (items: any): void => {
                if (items && items['hydra:member']) {
                    props.showMainLoader(false);

                    const item = items['hydra:member'].slice(0, 1).shift();

                    if (!item.accepted) {
                        NotificationHelper.notifyError('documents:invoices.invoice_not_accepted', dispatch);
                        return;
                    }

                    if (
                        (selectedDocumentType === 2 && item.buyer.id !== selectedReseller) ||
                        (selectedDocumentType === 4 && item.seller.id !== selectedReseller)
                    ) {
                        NotificationHelper.notifyError('documents:invoices.invoice_does_not_belong_to_reseller', dispatch);
                        return;
                    }

                    const itemData = item.dataRecords;
                    const readData: Array<InvoiceDataInterface> = [];
                    const editData: Array<InvoiceDataInterface> = [];
                    const tmpReseller = resellers['hydra:member'].find((reseller: PlatformResellerInterface) => reseller.id === selectedReseller);
                    if(!tmpReseller) {
                        return;
                    }
                    const tmpVat = findVatByPartialIri(vats, tmpReseller.vat);
                    _.each(itemData, (item: InvoiceDataInterface) => {
                        const tmpItem: InvoiceDataInterface = { ...item };
                        if (tmpItem.recordName !== 'Suma' && tmpItem.type === 1) {
                            const tmpReadItem = { ...tmpItem };
                            // eslint-disable-next-line no-undefined
                            tmpReadItem.amount = CurrencyHelper.bulletproofRound(item.amount, 2).toFixed(2);
                            // eslint-disable-next-line no-undefined
                            tmpReadItem.noTaxAmount = CurrencyHelper.bulletproofRound(item.noTaxAmount, 2).toFixed(2);
                            // eslint-disable-next-line no-undefined
                            tmpReadItem.taxAmount = CurrencyHelper.bulletproofRound(
                                CurrencyHelper.bulletproofRound(item.amount, 2) - CurrencyHelper.bulletproofRound(item.noTaxAmount, 2),
                                2
                            ).toFixed(2);

                            const tmpEditItem = { ...tmpItem, vat: tmpVat ? tmpVat['@id'] : '' };
                            let taxPercentageInt = parseInt(String(tmpVat?.percentage ?? 23));
                            let taxPercentage = (100 + (taxPercentageInt < 0 ? 0 : taxPercentageInt)) / 100;

                            tmpEditItem.noTaxAmount = CurrencyHelper.bulletproofRound(item.noTaxAmount, 2).toFixed(2);
                            tmpEditItem.amount = CurrencyHelper.bulletproofRound(
                                CurrencyHelper.bulletproofRound(tmpEditItem.noTaxAmount) * CurrencyHelper.bulletproofRound(taxPercentage),
                                2
                            ).toFixed(2);
                            if (taxPercentageInt === 0) {
                                tmpEditItem.taxAmount = CurrencyHelper.bulletproofRound('0').toFixed(2);
                            } else {
                                tmpEditItem.taxAmount = CurrencyHelper.bulletproofRound(
                                    CurrencyHelper.bulletproofRound(tmpEditItem.amount) - CurrencyHelper.bulletproofRound(tmpEditItem.noTaxAmount),
                                    2
                                ).toFixed(2);
                            }

                            readData.push({ ...tmpReadItem });
                            editData.push({ ...tmpEditItem });
                        }
                    });
                    console.log(readData);
                    setManualInvoiceReadData(readData);
                    setManualInvoiceData(editData);
                }
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [correctingInvoice, dispatch, itemLoader, props, resellers, selectedDocumentType, selectedReseller, vats]);


    return (
        <Dialog
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            keepMounted
            open={props.openView}
            fullWidth={props.fullWidth}
            maxWidth="lg"
        >
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <h4>
                        {props.title === null && ''}
                        {typeof props.title === 'string' ? <I18n trans={props.title} /> : props.title}
                    </h4>
                    <Button onClick={props.closeCallback} color="primary">
                        <Cancel width={24} height={24} />
                    </Button>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                <Card>
                    <CardHeader trans="documents:invoices.period" />
                    <CardBody>
                        <DateSelection />
                    </CardBody>
                    <CardHeader trans="documents:invoices.operator" />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={6} md={5} lg={5}>
                                {resellers && resellers['hydra:member'] && (
                                    <SimpleSelect
                                        label={'documents:invoices.operator'}
                                        required={true}
                                        elements={resellers['hydra:member'].map((item: PlatformResellerInterface) => {
                                            return { name: item.mainname, value: item.id };
                                        })}
                                        callback={selectReseller}
                                        dispatchName={'test'}
                                        selectedValue={selectedReseller}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                    <CardHeader trans="documents:invoices.document_type" />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={6} md={5} lg={5}>
                                {resellers && resellers['hydra:member'] && (
                                    <SimpleSelect
                                        label={'documents:invoices.document_type'}
                                        required={true}
                                        elements={[
                                            { name: 'documents:invoices.type.1', value: 1 },
                                            { name: 'documents:invoices.type.2', value: 2 },
                                            { name: 'documents:invoices.type.3', value: 3 },
                                            { name: 'documents:invoices.type.4', value: 4 },
                                        ]}
                                        callback={selectDocumentType}
                                        dispatchName={'test'}
                                        selectedValue={selectedDocumentType}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                    {selectedDocumentType && (selectedDocumentType === 3 || selectedDocumentType === 4) && (
                        <>
                            <CardHeader trans="documents:invoices.upload_file" />
                            <GridContainer>
                                <GridItem xs={12}>
                                    <label htmlFor="btn-upload">
                                        <input id="btn-upload" name="btn-upload" style={{ display: 'none' }} type="file" onChange={selectFile} />
                                        <Button className={`${classes.button} ${classes.lg} ${classes.primary}`} component="span">
                                            {file && file.name}
                                            {!file && <I18n trans="documents:invoices.select_cost_invoice_file" />}
                                        </Button>
                                    </label>
                                </GridItem>
                                <GridItem xs={6}>
                                    <InputForm
                                        stateName={'costInvoice'}
                                        callback={(event): void => {
                                            const target = event.target as HTMLInputElement;
                                            setCostInvoice((target.value as string).replace(/[\n\r\t]/g, ''));
                                        }}
                                        status={''}
                                        typeField={'text'}
                                        displayFieldName={'documents:invoices.invoice_number'}
                                        required={false}
                                        value={costInvoice}
                                        vMin={-1}
                                        vMax={256}
                                        typeCallback={'string'}
                                        autoComplete="documents_invoices_invoice_number"
                                    />
                                </GridItem>
                                <GridItem xs={6}>
                                    <InputForm
                                        stateName={'costBarcode'}
                                        callback={(event): void => {
                                            const target = event.target as HTMLInputElement;
                                            setCostBarcode((target.value as string).replace(/[\n\r\t]/g, ''));
                                        }}
                                        status={''}
                                        typeField={'text'}
                                        displayFieldName={'documents:invoices.barcode_number'}
                                        required={false}
                                        value={costBarcode}
                                        vMin={-1}
                                        vMax={256}
                                        typeCallback={'string'}
                                        autoComplete="documents_costBarcode"
                                    />
                                </GridItem>
                            </GridContainer>
                            <GridContainer>
                                <GridItem xs={4}>
                                    <CardHeader>
                                        <Typography variant="subtitle2">
                                            <I18n trans="documents:invoices.sold_on" />
                                        </Typography>
                                    </CardHeader>
                                    <CardBody>
                                        <DatePickerForm
                                            helpText="documents:invoices.sold_on"
                                            callback={(moment: Moment | string): void => {
                                                setSoldOn(DateHelper.formatYYYYMMDD(moment));
                                            }}
                                            timeFormat={false}
                                            dateFormat={'YYYY-MM-DD'}
                                            autoComplete={'off'}
                                        />
                                    </CardBody>
                                </GridItem>
                                <GridItem xs={4}>
                                    <CardHeader>
                                        <Typography variant="subtitle2">
                                            <I18n trans="documents:invoices.handed_on" />
                                        </Typography>
                                    </CardHeader>
                                    <CardBody>
                                        <DatePickerForm
                                            helpText="documents:invoices.handed_on"
                                            callback={(moment: Moment | string): void => {
                                                setHandedOn(DateHelper.formatYYYYMMDD(moment));
                                            }}
                                            timeFormat={false}
                                            dateFormat={'YYYY-MM-DD'}
                                            autoComplete={'off'}
                                        />
                                    </CardBody>
                                </GridItem>
                                <GridItem xs={4}>
                                    <CardHeader>
                                        <Typography variant="subtitle2">
                                            <I18n trans="documents:invoices.payment_deadline" />
                                        </Typography>
                                    </CardHeader>
                                    <CardBody>
                                        <DatePickerForm
                                            helpText="documents:invoices.payment_deadline"
                                            callback={(moment: Moment | string): void => {
                                                setPaymentDeadline(DateHelper.formatYYYYMMDD(moment));
                                            }}
                                            timeFormat={false}
                                            dateFormat={'YYYY-MM-DD'}
                                            autoComplete={'off'}
                                        />
                                    </CardBody>
                                </GridItem>
                            </GridContainer>

                            {selectedDocumentType === 4 && (
                                <GridContainer>
                                    <GridItem xs={6}>
                                        <InputForm
                                            stateName={'correctingInvoice'}
                                            callback={(event): void => {
                                                const target = event.target as HTMLInputElement;
                                                setCorrectingInvoice((target.value as string).replace(/[\n\r\t]/g, ''));
                                            }}
                                            status={''}
                                            typeField={'text'}
                                            displayFieldName={'documents:invoices.corrected_invoice_number'}
                                            required={false}
                                            value={correctingInvoice}
                                            vMin={-1}
                                            vMax={256}
                                            typeCallback={'string'}
                                            autoComplete="documents_correctingInvoice"
                                        />
                                        <CustomSmallTextButton
                                            callback={downloadCorrectedInvoiceData}
                                            label={'documents:invoices.download_invoice_data'}
                                        />
                                    </GridItem>
                                </GridContainer>
                            )}
                        </>
                    )}
                    {selectedDocumentType && selectedDocumentType === 2 && (
                        <GridContainer>
                            <GridItem xs={6}>
                                <SimpleSelect
                                    label={'documents:invoices.correction_type'}
                                    required={true}
                                    elements={[
                                        { name: 'documents:invoices.correction_types.1', value: 1 },
                                        { name: 'documents:invoices.correction_types.2', value: 2 },
                                    ]}
                                    callback={selectCorrectionType}
                                    dispatchName={'test'}
                                    selectedValue={selectedCorrectionType}
                                />
                            </GridItem>
                            <GridItem xs={6}>
                                <InputForm
                                    stateName={'correctionReason'}
                                    callback={(event): void => {
                                        const target = event.target as HTMLInputElement;
                                        setCorrectionReason((target.value as string).replace(/[\n\r\t]/g, ''));
                                    }}
                                    status={''}
                                    typeField={'text'}
                                    displayFieldName={'documents:invoices.correction_reason'}
                                    required={false}
                                    value={correctionReason}
                                    vMin={-1}
                                    vMax={1024}
                                    typeCallback={'string'}
                                    autoComplete="documents_correctingInvoice"
                                />
                            </GridItem>

                            {selectedCorrectionType && selectedCorrectionType === 2 && (
                                <GridContainer>
                                    <GridItem xs={6}>
                                        <InputForm
                                            stateName={'correctingInvoice'}
                                            callback={(event): void => {
                                                const target = event.target as HTMLInputElement;
                                                setCorrectingInvoice((target.value as string).replace(/[\n\r\t]/g, ''));
                                            }}
                                            status={''}
                                            typeField={'text'}
                                            displayFieldName={'documents:invoices.correcting_invoice'}
                                            required={false}
                                            value={correctingInvoice}
                                            vMin={-1}
                                            vMax={1024}
                                            typeCallback={'string'}
                                            autoComplete="documents_correctingInvoice"
                                        />
                                    </GridItem>
                                    <GridItem xs={6}>
                                        <CustomSmallTextButton
                                            callback={downloadCorrectedInvoiceData}
                                            label={'documents:invoices.download_invoice_data'}
                                        />
                                    </GridItem>
                                </GridContainer>
                            )}
                        </GridContainer>
                    )}
                    {vats && selectedDocumentType && (selectedDocumentType === 2 || selectedDocumentType === 4 || selectedDocumentType === 3) && (
                        <>
                            {manualInvoiceReadData && manualInvoiceReadData.length > 0 && (
                                <InvoiceDataList
                                    listKey={'manual_read'}
                                    readonly={true}
                                    correctionInvoice={true}
                                    header={
                                        selectedDocumentType === 4 || selectedDocumentType === 3
                                            ? 'documents:invoices.previous_cost_invoice_records'
                                            : 'documents:invoices.previous_invoice_records'
                                    }
                                    invoiceData={manualInvoiceReadData}
                                    vats={vats}
                                />
                            )}
                            {(selectedDocumentType === 4 ||
                                (selectedDocumentType === 2 && manualInvoiceData && manualInvoiceData.length > 0) ||
                                selectedDocumentType === 3) && (
                                <InvoiceDataList
                                    listKey={'manual_edit'}
                                    readonly={false}
                                    correctionInvoice={selectedDocumentType !== 4 && selectedDocumentType !== 3}
                                    header={
                                        selectedDocumentType === 4 || selectedDocumentType === 3
                                            ? selectedDocumentType === 4
                                                ? 'documents:invoices.cost_correction_invoice_records'
                                                : 'documents:invoices.cost_invoice_records'
                                            : 'documents:invoices.invoice_records'
                                    }
                                    headerCallback={selectedDocumentType === 4 || selectedDocumentType === 3 ? addInvoiceLine : undefined}
                                    headerCallbackTitle={
                                        selectedDocumentType === 4 || selectedDocumentType === 3 ? 'documents:invoices.add_invoice_line' : undefined
                                    }
                                    deleteCallback={selectedDocumentType === 4 || selectedDocumentType === 3 ? deleteInvoiceLine : undefined}
                                    invoiceData={manualInvoiceData}
                                    updateItem={updateCorrectionItem}
                                    vats={vats}
                                />
                            )}
                        </>
                    )}
                </Card>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={saveCallback} label="general:common.save" />
            </DialogActions>
        </Dialog>
    );
};

export default withStyles(oplAppStyle)(InvoiceAddDialog);
